import { useCallback, useEffect, useState } from "react";
import { verifyLeaf } from "@ondo/shared";
import type { WitnessLeaf, WitnessVerdict } from "@ondo/shared";

import { fetchWorkWitness } from "../lib/api/works.ts";
import { toWitnessPanel, witnessBundle } from "./witness.model.ts";
import type { WitnessLoad, WitnessPanel } from "./witness.model.ts";

/**
 * The Record tab's controller — OND-53. Reads the witness for one work, checks
 * every leaf IN THE BROWSER, and offers the bundle for checking elsewhere.
 *
 * THE CHECK IS DONE HERE AND NOT TRUSTED FROM THE SERVER, which is the whole
 * point of the tab: `verifyLeaf` recomputes every field hash, the leaf hash
 * and the climb to the root with `crypto.subtle`, so what the screen says
 * "matches" is what this machine computed and not what the API asserted.
 *
 * A FAILURE TO READ IS THE TAB'S OWN, never the record's: the panel says the
 * witness could not be read and the history beside it is untouched, for
 * `useWorkPhotographs`'s reason.
 */
const sha256 = async (bytes: Uint8Array): Promise<Uint8Array> =>
  // Copied into a fresh buffer: `digest` wants an ArrayBuffer-backed view and
  // a slice of a shared one is refused by the types before it is by the API.
  new Uint8Array(await crypto.subtle.digest("SHA-256", new Uint8Array(bytes)));

export interface WitnessController {
  panel: WitnessPanel;
  /** Null until there is something to download. */
  downloadBundle: (() => void) | null;
}

export function useWitnessController(workId: string): WitnessController {
  // Keyed by the work it was read for, so a change of work reads as loading
  // rather than as the previous work's leaves — without a synchronous reset
  // inside the effect, which the lint rule rightly refuses.
  const [read, setRead] = useState<{ workId: string; load: WitnessLoad }>({
    workId,
    load: { kind: "loading" },
  });
  const load: WitnessLoad = read.workId === workId ? read.load : { kind: "loading" };

  useEffect(() => {
    let active = true;

    fetchWorkWitness(workId)
      .then(async (leaves) => {
        const verdicts = new Map<string, WitnessVerdict>();
        for (const leaf of leaves) {
          verdicts.set(leaf.id, await verdictOf(leaf));
        }
        if (active) setRead({ workId, load: { kind: "loaded", leaves, verdicts } });
      })
      .catch(() => {
        if (active) setRead({ workId, load: { kind: "failed" } });
      });

    return () => {
      active = false;
    };
  }, [workId]);

  const downloadBundle = useCallback(() => {
    if (read.workId !== workId || read.load.kind !== "loaded") return;
    const blob = new Blob([witnessBundle(workId, read.load.leaves)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `ondo-witness-${workId}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [read, workId]);

  return {
    panel: toWitnessPanel(load),
    downloadBundle: load.kind === "loaded" && load.leaves.length > 0 ? downloadBundle : null,
  };
}

/** One leaf's verdict. A leaf the verifier cannot read is a failed check, not a crash. */
async function verdictOf(leaf: WitnessLeaf): Promise<WitnessVerdict> {
  if (leaf.preimage === null) return { kind: "shredded" };
  try {
    return await verifyLeaf(
      {
        preimage: leaf.preimage,
        salt: leaf.salt,
        leafHash: leaf.leafHash,
        path: leaf.path,
        root: leaf.root,
        revealed: leaf.revealed,
      },
      sha256,
    );
  } catch (error) {
    return { kind: "failed", reason: error instanceof Error ? error.message : String(error) };
  }
}
